import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Color } from "config/colors";
import { CompanyGig, CompanyGigApplicationWorker } from "model/Gig";
import { Gig } from "./gig.component";
import { PassedOrClosedGig } from "./passedOrClosedGig.component";
import { CustomIcon } from "components/icon/customIcon.component";
import { H4, Text } from "components/Typography/text.styled";
import {
  NoGigsWrapper,
  GigsWrapper,
  CheckBoxContainer,
  CheckboxText,
  FilterContainer,
  TextInput,
  ActionContainer,
  SearchContainer,
  FilterSearchContainer,
  ClearContainer,
} from "./gigs.styled";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { LoaderProgress } from "components/icon/loaderProgress.component";
import { Checkbox } from "@material-ui/core";
import { WorkerWithUrl } from "model/Company";
import { getWorkerImg } from "services/firebase/firebase.service";
import { Dropdown } from "components/dropdown/Dropdown";
import { FontFamily } from "config/font";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { Team } from "model/OrganizationalStructure";
import { DropDownContainer } from "../gigDetail/gigDetail.styled";
import { Pagination } from "../../general/pagination.component";

export const Gigs: React.FC = () => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [isLoading, setIsLoading] = useState(false);
  const [activeGigs, setActiveGigs] = useState<CompanyGig[]>([]);
  const [status, setStatus] = useState<"Active" | "Passed" | "Closed">(
    "Active"
  );
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const fetchAmount = 10;
  const [workers, setWorkers] = useState<CompanyGigApplicationWorker[]>([]);
  const [workerWithImageUrl, setWorkersWithImageUrl] =
    useState<WorkerWithUrl[]>();
  const [inputValue, setInputValue] = useState("");
  const [selectedWorkGroup, setSelectedWorkGroup] = useState<number | null>(
    null
  );
  const [workGroups, setWorkGroups] = useState<Team[]>([]);

  const [state] = useOrganizationalStructureStore();

  const allWorkGroups = useMemo(() => {
    return (state.structure?.entities ?? [])
      .flatMap((e) => e.units ?? [])
      .flatMap((u) => u.workGroups ?? []);
  }, [state.structure]);

  useEffect(() => {
    if (!companyState.company) return;
    setWorkGroups(
      Array.from(new Map(allWorkGroups.map((g) => [g.id, g])).values())
    );
  }, [companyState.company, allWorkGroups]);

  useEffect(() => {
    fetchGigs();
  }, [status, page, companyState.company, selectedWorkGroup]);

  const fetchGigs = () => {
    setActiveGigs([]);

    if (companyState.company?.id) {
      const id = companyState.company.id;
      setIsLoading(true);
      const sortAscending = status === "Active" ? true : false;
      const searchValue = inputValue !== "" ? inputValue : null;

      Api()
        .company.gig.getGigsWithState(
          id,
          status,
          sortAscending,
          page,
          fetchAmount,
          searchValue,
          selectedWorkGroup
        )
        .then((res) => {
          const workers = res.data.gigs.flatMap((gig: any) =>
            gig.applications.map((application: any) => application.worker)
          );

          setWorkers(workers);
          setTotalPages(Math.ceil(res.data.totalGigs / fetchAmount));
          setActiveGigs(res.data.gigs);
        })
        .catch((error) => {
          console.error("Error fetching gigs:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const onGigClosed = (gig: CompanyGig) => {
    setActiveGigs((old) => old.filter((g) => g.id !== gig.id));
  };

  useEffect(() => {
    if (!workers || workers.length === 0) return;

    const fetchWorkerImages = async () => {
      const updatedWorkers = await Promise.all(
        workers.map(async (worker) => {
          const url = worker.externalId
            ? await getWorkerImg(worker.externalId.toString())
            : "";
          const newWorker: WorkerWithUrl = {
            workerId: worker.id,
            firebaseId: worker.externalId,
            imageUrl: url || "",
          };
          return newWorker;
        })
      );
      setWorkersWithImageUrl(updatedWorkers);
    };

    fetchWorkerImages();
  }, [workers]);

  useEffect(() => {
    setPage(0);
  }, [status]);

  return (
    <>
      <ActionContainer>
        <FilterContainer style={{ margin: 10 }}>
          <CheckBoxContainer>
            <Checkbox
              checked={status === "Active"}
              onChange={() => setStatus("Active")}
            />
            <CheckboxText>{t("GigsCompany.Active")}</CheckboxText>
          </CheckBoxContainer>
          <CheckBoxContainer>
            <Checkbox
              checked={status === "Passed"}
              onChange={() => setStatus("Passed")}
            />
            <CheckboxText>{t("GigsCompany.Passed")}</CheckboxText>
          </CheckBoxContainer>
          <CheckBoxContainer>
            <Checkbox
              checked={status === "Closed"}
              onChange={() => setStatus("Closed")}
            />
            <CheckboxText>{t("GigsCompany.Archived")}</CheckboxText>
          </CheckBoxContainer>
        </FilterContainer>
        <FilterSearchContainer>
          <DropDownContainer style={{ width: 200, height: 35 }}>
            <Dropdown
              textColor={Color.MidnightBlue}
              smallerFont
              placeholder={t("WorkGroup.WorkGroup")}
              activeValue={selectedWorkGroup || undefined}
              items={workGroups
                .filter((m: any) => m.name !== "Favorites")
                .map((m: any) => ({
                  value: m.id,
                  label: m.name,
                  id: m.id,
                  workers: m.value,
                }))}
              maxHeight={200}
              onChange={(e: any) => {
                setSelectedWorkGroup(e.id);
              }}
            />
          </DropDownContainer>
          <SearchContainer>
            <TextInput
              value={inputValue}
              onChange={(e: any) => {
                setInputValue(e.currentTarget.value);
              }}
              style={{ height: 35, marginTop: 4 }}
            />
            <CustomIcon
              name="search"
              color={Color.LighterPositive}
              size="28px"
              style={{ margin: 6 }}
              onClick={() => {
                fetchGigs();
              }}
            />
          </SearchContainer>
          {(inputValue !== "" || selectedWorkGroup) && (
            <ClearContainer
              onClick={() => {
                setInputValue("");
                setSelectedWorkGroup(null);
              }}
            >
              <CustomIcon
                name="cross"
                color={Color.LighterDestructive}
                size="28px"
                style={{ margin: 6, marginTop: 12 }}
                onClick={() => {
                  fetchGigs();
                }}
              />
              <Text
                color={Color.LighterDestructive}
                fontFamily={FontFamily.MontserratSemiBold}
                style={{ cursor: "pointer", marginTop: 17 }}
              >
                {t("GigsCompany.Clear")}
              </Text>
            </ClearContainer>
          )}
        </FilterSearchContainer>
      </ActionContainer>
      <GigsWrapper loading={isLoading}>
        {activeGigs.map((gig) => {
          return status === "Active" ? (
            <Gig
              key={gig.id}
              gigData={gig}
              onGigClosed={onGigClosed}
              workerWithImageUrl={workerWithImageUrl}
            />
          ) : (
            <PassedOrClosedGig
              key={gig.id}
              gigData={gig}
              workerWithImageUrl={workerWithImageUrl}
            />
          );
        })}
        {!activeGigs.length && !isLoading && (
          <NoGigsWrapper>
            <CustomIcon name="document" size="130px" color={Color.SeaBlue400} />
            <H4>{t("GigsCompany.NoGigs")}</H4>
            <Text color={Color.SeaBlue400} padding>
              {t("GigsCompany.NoPublishedGigs")}
            </Text>
          </NoGigsWrapper>
        )}
      </GigsWrapper>
      {isLoading && <LoaderProgress />}
      {activeGigs.length !== 0 && (
        <Pagination setPage={setPage} page={page} totalPages={totalPages} />
      )}
    </>
  );
};
